/**
 * FontResolver - Maps (family, weight, style) → font_uid
 * 
 * Synchronous font resolution using pre-warmed cache from Font Manager API.
 * This ensures createTextRun() remains synchronous (critical for pagination).
 * 
 * Architecture:
 * 1. warmCache() called once on app mount (loads all enabled fonts)
 * 2. resolveFontUidSync() called during layout (synchronous cache lookup)
 * 3. clearCache() called when fonts are updated in Font Manager
 */

// Debug flag - set to false for production
const DEBUG_ENABLED = false;

import { getAllFonts, type RealmFont } from './FontAPI';
import { resolveFontUid as resolveFromManifest } from '../rendering/fonts/FontManifest';

interface FontCacheEntry {
  fontUid: string;
  displayName: string;
  category: string;
  courtCompliant: boolean;
  isCustom: boolean;
}

class FontResolver {
  private cache: Map<string, FontCacheEntry> = new Map();
  private loadPromise: Promise<void> | null = null;
  private isWarmed: boolean = false;

  // Configurable fallback font (defaults to system preferences document_font)
  private fallbackFontUid: string = 'Inter-Regular.ttf'; // Default until system preferences load
  
  /**
   * Set the fallback font UID to use when font resolution fails
   * Should be called after system preferences are loaded
   */
  setFallbackFont(fontUid: string): void {
    DEBUG_ENABLED && console.log(`[FontResolver] Setting fallback font to: ${fontUid}`);
    this.fallbackFontUid = fontUid;
  }

  /**
   * Get the current fallback font UID
   */
  getFallbackFont(): string {
    return this.fallbackFontUid;
  }

  /**
   * Warm the cache by loading all enabled fonts from Font Manager API
   * Should be called once on app mount, before any rendering
   */
  async warmCache(): Promise<void> {
    // Prevent duplicate warming
    if (this.loadPromise) {
      DEBUG_ENABLED && console.log('[FontResolver] Cache warming already in progress, awaiting...');
      return this.loadPromise;
    }
    
    if (this.isWarmed) {
      DEBUG_ENABLED && console.log('[FontResolver] Cache already warmed');
      return;
    }
    
    DEBUG_ENABLED && console.log('[FontResolver] 🔥 Starting cache warmup...');
    const startTime = performance.now();

    this.loadPromise = (async () => {
      DEBUG_ENABLED && console.log('[FontResolver] Calling getAllFonts() API...');
      try {
        // Load all fonts from Font Manager API
        const fonts = await getAllFonts();
        DEBUG_ENABLED && console.log(`[FontResolver] getAllFonts() returned ${fonts.length} fonts:`, fonts.map(f => f.fontUid));

        // Only cache enabled fonts
        const enabledFonts = fonts.filter(f => f.enabled);
        
        DEBUG_ENABLED && console.log(`[FontResolver] Loaded ${enabledFonts.length} enabled fonts from Font Manager`);
        
        // Build cache: "Family-Weight-Style" → FontCacheEntry
        for (const font of enabledFonts) {
          const key = this.buildCacheKey(font.fontFamily, font.weight, font.style);
          
          if (!font.enabled) continue; // ensure only enabled variants are cached
          this.cache.set(key, {
            fontUid: font.fontUid,
            displayName: font.displayName,
            category: font.category,
            courtCompliant: font.courtCompliant,
            isCustom: font.isCustom
          });
        }
        
        const duration = performance.now() - startTime;
        DEBUG_ENABLED && console.log(`[FontResolver] ✅ Cache warmed with ${this.cache.size} font variants in ${duration.toFixed(0)}ms`);
        
        this.isWarmed = true;
        
        // Log cache contents for debugging
        if (process.env.NODE_ENV !== 'production') {
          DEBUG_ENABLED && console.log('[FontResolver] Cache contents:', Array.from(this.cache.keys()));
        }
        
      } catch (error) {
        console.error('[FontResolver] ❌ Failed to warm cache:', error);
        // Don't throw - allow app to continue with fallback fonts
        this.isWarmed = false;
      } finally {
        this.loadPromise = null;
      }
    })();
    
    return this.loadPromise;
  }
  
  /**
   * Build cache key from font spec
   * Format: "fontfamily-Weight-Style" (lowercase family for case-insensitive lookup)
   */
  private buildCacheKey(family: string, weight: number, style: string): string {
    // Lowercase family name for case-insensitive matching
    return `${family.toLowerCase()}-${weight}-${style}`;
  }
  
  /**
   * Synchronous font resolution using cache
   * CRITICAL: This must remain synchronous for pagination to work!
   * 
   * @param family - Font family name (e.g., "Carlito")
   * @param weight - Font weight (e.g., 400, 700)
   * @param style - Font style ("normal", "italic", "oblique")
   * @returns font_uid for backend lookup (e.g., "Carlito-Bold.ttf")
   */
  resolveFontUidSync(
    family: string, 
    weight: number = 400, 
    style: string = 'normal'
  ): string {
    // Normalize family: strip quotes, trim whitespace, drop display suffixes like "Inter - Modern Sans-Serif"
    const cleanFamily = (family || '')
      // strip leading/trailing single or double quotes
      .replace(/^\s*[\'\"]/g, '')
      .replace(/[\'\"]\s*$/g, '')
      // prefer the first family if a comma-separated list was provided
      .split(',')[0]
      // drop display suffixes like "Inter - Modern Sans-Serif"
      .split(' - ')[0]
      .trim();

    // Note: Case normalization happens in buildCacheKey() for case-insensitive lookup

    DEBUG_ENABLED && console.log(`[FontResolver] 🔍 Resolving: "${cleanFamily}" ${weight} ${style}`);
    
    // Check if cache is warmed
    if (!this.isWarmed) {
      DEBUG_ENABLED && console.warn('[FontResolver] ⚠️ Cache not warmed yet, using fallback font');
      DEBUG_ENABLED && console.log('[FontResolver] Cache stats:', this.getCacheStats());
      return this.fallbackFontUid;
    }
    
    // Try exact match first
    const exactKey = this.buildCacheKey(cleanFamily, weight, style);
    DEBUG_ENABLED && console.log(`[FontResolver] Looking for exact key: "${exactKey}"`);
    const exactMatch = this.cache.get(exactKey);
    
    if (exactMatch) {
      DEBUG_ENABLED && console.log(`[FontResolver] ✅ Found exact match: ${exactMatch.fontUid}`);
      return exactMatch.fontUid;
    }
    
    // 🆕 2026-01-21: Try static FontManifest as fallback before giving up
    // This handles cases where font variants (bold, italic) aren't in the database
    // but exist in the static manifest (e.g., Inter-Bold.ttf)
    const manifestFontUid = resolveFromManifest(cleanFamily, weight, style as 'normal' | 'italic' | 'oblique');
    if (manifestFontUid) {
      console.log(
        `[FontResolver] Cache miss for ${exactKey}, resolved from static manifest: ${manifestFontUid}`
      );
      return manifestFontUid;
    }

    // Try fallback to Regular variant of same family
    const regularKey = this.buildCacheKey(cleanFamily, 400, 'normal');
    DEBUG_ENABLED && console.log(`[FontResolver] Looking for regular variant: "${regularKey}"`);
    const regularMatch = this.cache.get(regularKey);

    if (regularMatch) {
      console.warn(
        `[FontResolver] Cache miss for ${exactKey}, falling back to ${regularKey}`
      );
      return regularMatch.fontUid;
    }

    // Debug: show what IS in the cache
    console.error(`[FontResolver] ❌ No match found for family "${cleanFamily}"`);
    const availableFamilies = this.getCachedFamilies();
    DEBUG_ENABLED && console.log('[FontResolver] Available families:', availableFamilies);
    DEBUG_ENABLED && console.log('[FontResolver] Total cache entries:', this.cache.size);
    DEBUG_ENABLED && console.log('[FontResolver] Cache keys sample:', Array.from(this.cache.keys()).slice(0, 10));

    // Font resolution failed - will use system preferences fallback

    // Last resort: use fallback font (system preferences default)
    console.warn(
      `[FontResolver] Using system preferences fallback: ${this.fallbackFontUid}`
    );
    return this.fallbackFontUid;
  }
  
  /**
   * Get font metadata from cache (useful for debugging)
   */
  getFontMetadata(family: string, weight: number, style: string): FontCacheEntry | null {
    const key = this.buildCacheKey(family, weight, style);
    return this.cache.get(key) || null;
  }
  
  /**
   * Check if a specific font variant is cached
   */
  isCached(family: string, weight: number, style: string): boolean {
    const key = this.buildCacheKey(family, weight, style);
    return this.cache.has(key);
  }
  
  /**
   * Get all cached font families (unique family names)
   */
  getCachedFamilies(): string[] {
    const families = new Set<string>();
    for (const key of this.cache.keys()) {
      const [family] = key.split('-');
      families.add(family);
    }
    return Array.from(families).sort();
  }
  
  /**
   * Get all cached variants for a specific family
   */
  getCachedVariants(family: string): Array<{ weight: number; style: string; fontUid: string }> {
    const variants: Array<{ weight: number; style: string; fontUid: string }> = [];
    
    for (const [key, entry] of this.cache.entries()) {
      const [cachedFamily, weightStr, style] = key.split('-');
      if (cachedFamily === family) {
        variants.push({
          weight: parseInt(weightStr, 10),
          style,
          fontUid: entry.fontUid
        });
      }
    }
    
    return variants.sort((a, b) => {
      // Sort by weight first, then by style
      if (a.weight !== b.weight) return a.weight - b.weight;
      return a.style.localeCompare(b.style);
    });
  }
  
  /**
   * Clear cache (call when fonts are updated in Font Manager)
   */
  clearCache(): void {
    DEBUG_ENABLED && console.log('[FontResolver] Clearing cache...');
    this.cache.clear();
    this.isWarmed = false;
    this.loadPromise = null;
  }
  
  /**
   * Invalidate and re-warm cache (call after font upload/delete)
   */
  async refreshCache(): Promise<void> {
    DEBUG_ENABLED && console.log('[FontResolver] Refreshing cache...');
    this.clearCache();
    await this.warmCache();
  }
  
  /**
   * Get cache statistics (for debugging)
   */
  getCacheStats(): {
    isWarmed: boolean;
    totalEntries: number;
    totalFamilies: number;
  } {
    return {
      isWarmed: this.isWarmed,
      totalEntries: this.cache.size,
      totalFamilies: this.getCachedFamilies().length
    };
  }
}

// Singleton instance
export const fontResolver = new FontResolver();

// Export class for testing
export { FontResolver };

